import React from 'react';
import styled, { css } from 'styled-components';
import { MultipleChoiceFilter, FilterGroup } from '@lt/components';
import { StyledFilterLabel } from '@lt/components/components/FilterLabel/FilterLabel';
import { i18n } from '@lt/localization';
import {
  FamilySelected,
  CouplesSelected,
  ActiveSelected,
} from '@lt/components/icons-react/userflow/filters';

import type { IFilter } from '../types';

const FILTER_NAME = 'category';
const FILTER_LABEL = i18n.t('filters.category.title');

const REST_TYPES = {
  FAMILY: i18n.t('filters.category.family'),
  COUPLES: i18n.t('filters.category.couples'),
  ACTIVE: i18n.t('filters.category.active'),
};

const StyledCategoryIcon = css<{
  $active: boolean;
}>`
  margin-bottom: 3px;
  height: 55px;
  text-align: center;

  svg {
    height: 100%;
  }
  ${({ $active }) =>
    !$active &&
    `
    path {
      fill: none;
      stroke: #919499;
    }
  `};
`;

const IconFamily = styled(FamilySelected)`
  ${StyledCategoryIcon}
`;

const IconCouples = styled(CouplesSelected)`
  ${StyledCategoryIcon}
`;

const IconActive = styled(ActiveSelected)`
  ${StyledCategoryIcon}
`;

const ICON_TYPES = {
  FAMILY: IconFamily,
  COUPLES: IconCouples,
  ACTIVE: IconActive,
};

type FilterCategoryLabelProps = {
  id: string;
  selected: boolean;
  disabled: boolean;
  isMobile: boolean;
};

const FilterCategoryLabel = ({
  id,
  selected,
  disabled,
  isMobile,
}: FilterCategoryLabelProps) => {
  const CategoryIcon = ICON_TYPES[id];

  return (
    <StyledFilterItem>
      <CategoryIcon $active={selected} />
      <StyledFilterLabel
        disabled={disabled}
        selected={selected}
        isMobile={isMobile}
      >
        {REST_TYPES[id]}
      </StyledFilterLabel>
    </StyledFilterItem>
  );
};

const StyledFilterItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledMultipleChoiceFilter = styled(MultipleChoiceFilter)`
  display: flex;
  justify-content: space-between;

  label {
    > :not(${StyledFilterItem}) {
      display: none;
    }
  }

  ${({ isMobile }) =>
    isMobile &&
    `
      justify-content: space-around;

      label {
        border: none !important;
      }
    `}
`;

const FilterCategory = ({
  data,
  className = '',
  isMobile = false,
  onChange,
  onClear,
  visibleItems,
}: IFilter): JSX.Element => (
  <FilterGroup
    className={className}
    label={FILTER_LABEL}
    onClear={() => onClear(FILTER_NAME)}
    showClearBtn={data.some((item) => item.selected)}
    isMobile={isMobile}
  >
    <StyledMultipleChoiceFilter
      isMobile={isMobile}
      items={data}
      className={`filter-${FILTER_NAME}`}
      onChange={(diff) => onChange({ path: FILTER_NAME, diff })}
      visibleItems={visibleItems}
      LabelComponent={FilterCategoryLabel}
    />
  </FilterGroup>
);

export default Object.assign(FilterCategory, {
  filterName: FILTER_NAME,
  filterLabel: FILTER_LABEL,
  valuesLabel: (data) =>
    data
      .filter((item) => item.selected)
      .map((item) => REST_TYPES[item.id])
      .reduce((prev, curr) => [prev, ', ', curr]),
});
